import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Alert, LoadingIndicator } from 'src/latest/src/components';
import { Logger } from 'src/latest/src/services';
import './StripePayment.scss';
import {
  editStripeCardNickName,
  getStripePaymentMethods,
  stripeCardDelete,
  stripeSetCardAsDefault,
  stripeSetupIntent,
} from 'src/redux/services/commonServices';
import { PaymentMethod, SetupIntentResponse } from './StripePaymentInterface';
import StripeAdd from './components/StripeAdd';
import StripeUpdate from './components/StripeUpdate';
import StripeView from './components/StripeView/StripeView';
import { useTranslation } from 'react-i18next';
import {
  StripeOnTokenMethod,
  StripePaymentMode,
} from './StripePaymentConstant';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserAddressByIdV1, getUserDataById } from 'src/api/user';
import { User } from 'src/latest/src/components/UserProfileHeader/types';
import { setFlashNotification } from 'src/redux/modules/flashNotification';
import { updateContinueButton } from 'src/redux/actions/questionnaireActions';

interface StripePaymentProps {
  choices?: any[];
  onToken?: (status: string, choice: any) => void;
  fromOnboarding: boolean;
  manageCardClientId?: string;
  onCardStatusChange?: (hasCard: boolean) => void;
  onCardFormValidationChange?: (isValid: boolean) => void;
  onStripeProcessingChange?: (isProcessing: boolean) => void;
}

const StripePayment = forwardRef<any, StripePaymentProps>((props, ref) => {
  const {
    choices,
    onToken,
    fromOnboarding,
    manageCardClientId,
    onCardStatusChange,
    onCardFormValidationChange,
    onStripeProcessingChange,
  } = props;
  const choice = choices ? choices?.[0] : [];
  const dispatch = useDispatch();
  const [existingCard, setExistingCard] = useState<[PaymentMethod] | null>(
    null,
  );
  // Commenting this as we need to disable the continue button irrespective of platform i.e., directory or not. But we can re-use this later if needed.
  // const isDirectoryPlatform = useSelector(
  //   (state: any) => state.configuration.isDirectoryPlatform === 'true',
  // );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupIntent, setSetupIntent] = useState<SetupIntentResponse | null>(
    null,
  );
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [mode, setMode] = useState<StripePaymentMode>(StripePaymentMode.VIEW);
  const { t } = useTranslation('stripePayment');
  const [accountAddress, setAccountAddress] = useState<any>({});
  const { initialMetadata } = useSelector(
    (state: any) => state?.questionnaireData,
  );
  const clientId = fromOnboarding
    ? initialMetadata?.user_id
    : manageCardClientId;
  const [responsiblePartyAccountAddress, setResponsiblePartyAccountAddress] =
    useState<any>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [hideBackButton, setHideBackButton] = useState<boolean>(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [nickname, setNickname] = useState<string>('');
  const [nicknameError, setNicknameError] = useState<string>('');
  const [isUpdatingNickname, setIsUpdatingNickname] = useState<boolean>(false);
  const [isCardFormValid, setIsCardFormValid] = useState<boolean>(false);
  const stripeCardRef = useRef<any>(null);

  useImperativeHandle(
    ref,
    () => ({
      triggerAddCardMode: () => {
        setMode(StripePaymentMode?.ADD);
        createSetupIntent();
      },
      submitCard: () => {
        stripeCardRef.current?.submitCard();
      },
    }),
    [],
  );

  const fetchPaymentMethods = async (consent = '') => {
    try {
      setIsLoading(true);
      const response = await getStripePaymentMethods(clientId);
      const hasCard = response.payment_methods?.length > 0;

      if (hasCard) {
        onCardStatusChange?.(true);

        setHideBackButton(false);
        setExistingCard(response.payment_methods);

        if (fromOnboarding && onToken && choices) {
          onToken(
            consent === ''
              ? StripeOnTokenMethod?.SUCCESS
              : 'consent_accepted: true',
            choice,
          );
        }

        setMode(StripePaymentMode?.VIEW);
      } else {
        onCardStatusChange?.(false);

        if (fromOnboarding && onToken && choices) {
          dispatch(
            updateContinueButton({
              buttonStatus: 'disabled',
            }),
          );
        }

        setHideBackButton(true);

        const userData = await getUserDataById(clientId, true, 'small');

        setUserData(userData);

        if (
          userData?.metadata &&
          userData.metadata.responsible_party_zipcode &&
          userData.metadata.responsible_party_zipcode.length > 0
        ) {
          setAccountAddress(null);
          setResponsiblePartyAccountAddress(userData.metadata);
        } else {
          const addressData = await fetchUserAddressByIdV1(clientId);
          const mergedAddressData =
            addressData && Object.keys(addressData).length > 0
              ? {
                  ...addressData,
                  name: userData.metadata.responsible_party_name
                    ? userData.metadata.responsible_party_name
                    : userData?.full_name,
                }
              : {};

          setResponsiblePartyAccountAddress(null);
          setAccountAddress(mergedAddressData);
        }

        setMode(StripePaymentMode?.ADD);
        createSetupIntent();
      }
    } catch (error) {
      onCardStatusChange?.(false);
      Logger.error(`${t('stripePayment.errorMsg.failToFetch')}`, error);
      setError(t('stripePayment.errorMsg.failToLoad'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    onCardFormValidationChange?.(isCardFormValid);
  }, [isCardFormValid, onCardFormValidationChange]);

  const createSetupIntent = async () => {
    try {
      setIsLoading(true);
      const response = await stripeSetupIntent(clientId);

      setSetupIntent(response);

      if (response.publishable_key) {
        setStripePromise(
          loadStripe(response.publishable_key, {
            stripeAccount: response.stripe_connect_account_id,
          }),
        );
      }
    } catch (error) {
      Logger.error(t('stripePayment.errorMsg.failToSetup'), error);
      setError(t('stripePayment.errorMsg.failToInitialize'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditNickname = (card: PaymentMethod) => {
    setEditingCardId(card.id);
    setNickname(card.card_nick_name || '');
    setNicknameError('');
  };

  const handleSaveNickname = async (cardId: string) => {
    const trimmedNickname = nickname.trim();

    if (trimmedNickname === '') {
      setNicknameError(t('stripePayment.errorMsg.nicknameCannotBeEmpty'));

      return;
    }

    const currentCard = existingCard?.find(card => card.id === cardId);

    if (currentCard?.card_nick_name === trimmedNickname) {
      setEditingCardId(null);

      return;
    }

    setIsUpdatingNickname(true);

    try {
      await editStripeCardNickName(clientId, cardId, trimmedNickname);

      if (existingCard) {
        const updatedCards = existingCard.map(card =>
          card.id === cardId
            ? { ...card, card_nick_name: trimmedNickname }
            : card,
        );

        setExistingCard(updatedCards as [PaymentMethod]);
      }

      setEditingCardId(null);

      setNickname('');

      dispatch(
        setFlashNotification({
          message: t('stripePayment.successMsg.updatedSuccessfully'),
        }),
      );
    } catch (error) {
      setNicknameError(t('stripePayment.errorMsg.failToUpdateNickname'));
      Logger.error(t('stripePayment.errorMsg.failToUpdateNickname'), error);
    } finally {
      setIsUpdatingNickname(false);
    }
  };

  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNickname(e.target.value);
    setNicknameError('');
  };

  const handleNicknameKeyPress = (e: React.KeyboardEvent, cardId: string) => {
    if (e.key === 'Enter') {
      handleSaveNickname(cardId);
    } else if (e.key === 'Escape') {
      setEditingCardId(null);
      setNickname('');
      setNicknameError('');
    }
  };

  const handleCancelNicknameEdit = () => {
    setEditingCardId(null);
    setNickname('');
    setNicknameError('');
  };

  const handleUpdateCard = async () => {
    setIsLoading(true);

    const userData = await getUserDataById(clientId, true, 'small');

    setUserData(userData);

    if (
      userData?.metadata &&
      userData.metadata.responsible_party_zipcode &&
      userData.metadata.responsible_party_zipcode.length > 0
    ) {
      setAccountAddress(null);
      setResponsiblePartyAccountAddress(userData?.metadata);
    } else {
      const addressData = await fetchUserAddressByIdV1(clientId);
      const mergedAddressData =
        addressData && Object.keys(addressData).length > 0
          ? {
              ...addressData,
              name: userData.metadata.responsible_party_name
                ? userData.metadata.responsible_party_name
                : userData?.full_name,
            }
          : null;

      setResponsiblePartyAccountAddress(null);
      setAccountAddress(mergedAddressData);
    }

    setMode(StripePaymentMode?.UPDATE);
    createSetupIntent();
    setIsLoading(false);
  };

  const handleCardUpdated = (consent = '') => {
    onStripeProcessingChange?.(false);
    fetchPaymentMethods(consent);
  };

  const handleLeftArrowClick = () => {
    setMode(StripePaymentMode.VIEW);
  };

  const handleSetDefaultCard = async (cardId: string) => {
    setIsLoading(true);

    try {
      await stripeSetCardAsDefault(clientId, cardId);
      fetchPaymentMethods();
      dispatch(
        setFlashNotification({
          message: t('stripePayment.successMsg.setAsDefault'),
        }),
      );
    } catch (error: any) {
      setError(
        error?.message ||
          error?.response?.data?.message ||
          t('stripePayment.errorMsg.failToSetDefault'),
      );
      setIsLoading(false);
    }
  };

  const handleCardDelete = async (cardId: string) => {
    setIsLoading(true);
    onStripeProcessingChange?.(true);

    try {
      await stripeCardDelete(clientId, cardId);
      await fetchPaymentMethods();
      dispatch(
        setFlashNotification({
          message: t('stripePayment.successMsg.deletedSuccessfully'),
        }),
      );
    } catch (error: any) {
      setError(
        error?.message ||
          error?.response?.data?.message ||
          t('stripePayment.errorMsg.failToDelete'),
      );
      setIsLoading(false);
    } finally {
      onStripeProcessingChange?.(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className={`stripe-payment-container ${
          fromOnboarding ? `from-onboarding` : ``
        }`}
      >
        <LoadingIndicator loading={true} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`stripe-payment-container ${
          fromOnboarding ? `from-onboarding` : ``
        }`}
      >
        <div className="stripe-payment-container-error">
          <Alert
            className="stripe-payment-container-error-alert"
            type="error"
            message={error}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`stripe-payment-container ${
        mode === StripePaymentMode?.VIEW && existingCard ? `view-width` : ``
      } ${fromOnboarding ? `from-onboarding` : ``}`}
    >
      {mode === StripePaymentMode?.VIEW && existingCard && (
        <StripeView
          existingCard={existingCard}
          handleUpdateCard={handleUpdateCard}
          handleCardDelete={handleCardDelete}
          handleSetDefaultCard={handleSetDefaultCard}
          clientId={clientId}
          editingCardId={editingCardId}
          nickname={nickname}
          nicknameError={nicknameError}
          isUpdatingNickname={isUpdatingNickname}
          onEditNickname={handleEditNickname}
          onSaveNickname={handleSaveNickname}
          onNicknameChange={handleNicknameChange}
          onNicknameKeyPress={handleNicknameKeyPress}
          onCancelNicknameEdit={handleCancelNicknameEdit}
          hideUpdateButton={fromOnboarding}
        />
      )}

      {mode === StripePaymentMode?.UPDATE && (
        <StripeUpdate
          setupIntent={setupIntent}
          stripePromise={stripePromise}
          onToken={onToken}
          choice={choice}
          handleCardUpdated={handleCardUpdated}
          accountAddress={accountAddress}
          responsiblePartyAccountAddress={responsiblePartyAccountAddress}
          fromOnboarding={fromOnboarding}
          userData={userData}
          handleLeftArrowClick={handleLeftArrowClick}
          hideBackButton={hideBackButton}
          isDirectoryPlatform={true}
          onStripeProcessingChange={onStripeProcessingChange}
        />
      )}

      {mode === StripePaymentMode?.ADD && (
        <StripeAdd
          setupIntent={setupIntent}
          stripePromise={stripePromise}
          onToken={onToken}
          choice={choice}
          handleCardUpdated={handleCardUpdated}
          accountAddress={accountAddress}
          responsiblePartyAccountAddress={responsiblePartyAccountAddress}
          fromOnboarding={fromOnboarding}
          userData={userData}
          handleLeftArrowClick={handleLeftArrowClick}
          hideBackButton={hideBackButton}
          isDirectoryPlatform={true}
          onFormValidationChange={setIsCardFormValid}
          stripeCardRef={stripeCardRef}
          onStripeProcessingChange={onStripeProcessingChange}
        />
      )}
    </div>
  );
});

export default StripePayment;
